import { useId, type ReactNode } from 'react';
import { cx } from '../cx';
import { ErrorText } from './ErrorText';

export type FieldRenderProps = {
  id: string;
  /** ids for aria-describedby: help and error text */
  describedBy: string | undefined;
  invalid: boolean;
  /** id of the visible label, for controls that need aria-labelledby (e.g. a slider beside the field) */
  labelId: string;
};

export type FieldProps = {
  /** Visible label. Required: a placeholder is an example, never the label. */
  label: ReactNode;
  /** Keeps the label for screen readers only, where the surrounding UI already labels the field. */
  labelHidden?: boolean;
  help?: ReactNode;
  /** Says what went wrong and how to fix it. Shows a warning icon in the field, a dashed ink outline (there is no red) and sets aria-invalid. */
  error?: ReactNode;
  id?: string;
  className?: string;
  children: (props: FieldRenderProps) => ReactNode;
};

export type FieldSize = 'md' | 'sm';

/** `md` is the 48px form field (16px text, so iOS never zooms); `sm` is the 40px toolbar field (14px text, for desktop filter rows). */
export const fieldControlClass = (invalid: boolean, size: FieldSize = 'md') =>
  cx(
    size === 'sm' ? 'h-10 text-sm' : 'h-12 text-base',
    'w-full min-w-0 rounded-jc-field border bg-jc-surface px-4 text-jc-ink placeholder:text-jc-ink-secondary',
    'transition-colors duration-(--jc-duration-fast) focus-visible:border-jc-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-jc-focus focus-visible:ring-offset-2 focus-visible:ring-offset-jc-page',
    'disabled:cursor-not-allowed disabled:opacity-50',
    // Invalid is a dashed ink border (no ring) plus an in-control warning icon, so it never looks like the solid focus ring.
    invalid ? 'border-2 border-dashed border-jc-ink' : 'border-jc-border-control',
  );

/** Label, control, help and error, wired together by id. Input, Select and RangeSlider build on it. */
export function Field({ label, labelHidden, help, error, id: idProp, className, children }: FieldProps) {
  const auto = useId();
  const id = idProp ?? `f${auto.replace(/:/g, '')}`;
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, helpId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('flex flex-col gap-1.5', className)}>
      <label
        id={`${id}-label`}
        htmlFor={id}
        className={cx('text-sm font-semibold text-jc-ink', labelHidden && 'sr-only')}
      >
        {label}
      </label>
      {children({ id, describedBy, invalid: Boolean(error), labelId: `${id}-label` })}
      {error ? <ErrorText id={errorId}>{error}</ErrorText> : null}
      {help ? (
        <p id={helpId} className="text-xs text-jc-ink-secondary">
          {help}
        </p>
      ) : null}
    </div>
  );
}
